/**
 * The base-currency equivalent of a document total, the way Xero prints it.
 *
 * Xero shows a foreign-currency document beside the organisation's own base
 * currency:
 *
 *     1 GBP = 1.89207 AUD
 *     Total   AUD 17,500.00
 *             GBP  9,248.66
 *
 * This module owns the ARITHMETIC and the VOCABULARY for that. It renders
 * nothing and fetches nothing: the base currency arrives app-wide on
 * `GET /xero/connection` as `baseCurrency`, and the rate is the document's own
 * `currencyRate` (`DECIMAL(18,6)`, nullable, writable — see `types.ts`).
 *
 * ⚠️ **There is NO rate feed and there must not be one.** The owner ruled out
 * the external currency API by name — *"do not use the currency rate api which i
 * gave, use the system one only"* — so the only rate that exists is the one
 * already stored on the record, or one a person types into it. Nothing here
 * carries a provider, a fetch timestamp or a staleness check, and nothing should
 * grow one: a field nothing populates reads as a feature that has broken.
 *
 * Two hazards shape every line below, and they are the whole reason this is a
 * module rather than three lines at a call site.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ 1. MONEY NEVER SEES A FLOAT — AND A RATE IS MONEY HERE
 *
 * `Number("17500.00") / 1.89498` is exactly the arithmetic this repo has banned
 * everywhere else. It does not throw, it does not warn, and it disagrees with
 * the figure Xero prints by a penny once the numbers get large. A rate is a
 * decimal string for the same reason an amount is, and it must stay one all the
 * way through the division. Everything here is scaled `BigInt`, as in
 * `decimal.ts`.
 *
 * ⚠️ **Why the scaled-BigInt primitives are re-implemented here rather than
 * imported.** `decimal.ts` keeps `parseDecimal`, `formatScaled` and
 * `divideRoundHalf` private, and it has no division-of-two-decimals primitive at
 * all — its two exported operations are a sum and a percentage. Adding one there
 * would widen a file that four money surfaces already depend on, for a single
 * consumer. So the ~40 lines are duplicated deliberately and kept byte-compatible
 * in behaviour: same regex, same "round half away from zero", same
 * `null`-propagation. If `decimal.ts` ever grows a real `divideDecimalStrings`,
 * delete the copies here and import it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ 2. THE DIRECTION OF `currencyRate` IS AMBIGUOUS, AND BACKWARDS REACHES
 *       XERO — NOT JUST OUR SCREEN
 *
 * "1 GBP = 1.89207 AUD" can be stored as `1.89207` or as its inverse
 * `0.528522`, and both are honest readings of a bare column called
 * `currencyRate`. Nothing on the wire records which. Applied the wrong way round
 * on this example, a 17,500 AUD quote reads as **33,111 GBP** instead of
 * **9,249 GBP** — a plausible-looking figure, wrong by a factor of three and a
 * half. Nothing typechecks it and nothing logs it.
 *
 * ⚠️ **And it does not stop at our screen. This field is PUSHED.** Measured in
 * the backend source: `xero-quote.payload.ts:772` and
 * `xero-invoice.payload.ts:817` both do
 *
 *     if (input.quote.currencyRate) payload.CurrencyRate = Number(input.quote.currencyRate);
 *
 * — our column, unconverted, straight onto `Quote.CurrencyRate` /
 * `Invoice.CurrencyRate`. So `currencyRate` is **not a display field of ours**.
 * Whatever a person types into it lands on a real document in SEED's accounting
 * system, where an accountant reads it and nothing on our side can withdraw it.
 * A UI that writes this field is writing to Xero.
 *
 * So the direction is stated in the TYPE. {@link ExchangeRate} is not two bare
 * strings and a number; its field is called `ratePerBase` and its doc comment
 * says which side of the equals sign it belongs to. A caller cannot pass the
 * arguments the wrong way round, because there is only one argument and it is
 * self-describing. {@link exchangeRateOf} is the ONE place the app's
 * `currencyRate` is interpreted as a direction — if the answer below comes back
 * the other way, that function is the single line to change.
 *
 * ⚠️ **THE OPEN QUESTION, STATED PRECISELY, AND IT IS WITH THE BACKEND: given
 * that the push sends our `currencyRate` UNCONVERTED, does `ratePerBase` match
 * the orientation Xero means by `CurrencyRate` — units of document currency per
 * 1 base, or the inverse?** What is settled is only that the two are the same
 * number; which direction that number faces is not. This module ASSUMES
 * `ratePerBase` because that is the reading that matches the line Xero's own UI
 * prints — a reading of a screenshot, not a contract. The `ratePerBase` naming
 * asserts what THIS MODULE means, so that call sites cannot be ambiguous; it
 * asserts nothing about Xero, and must not be read as having answered the
 * question.
 *
 * **Do not resolve it by guessing**, and do not "confirm" it by observing that a
 * converted figure looks about right: at rates near 1 both directions look about
 * right, which is precisely how this ships wrong. Ask, and record the answer
 * here.
 *
 * There is deliberately **no `invertRate()` export**. Offering one invites the
 * next person to fix a direction problem by flipping the number at a call site,
 * which spreads the ambiguity instead of resolving it. If the stored value turns
 * out to be the inverse, invert it once, inside `exchangeRateOf`, where the
 * interpretation lives.
 */

/**
 * The backend validates `currencyRate` as non-negative with at most six decimal
 * places (`optionalCurrencyRate()`), and Xero's own UI shows six (`1.89207`,
 * `0.528522`). Exported so a rate input can cap its own precision rather than
 * discovering the limit as a 422.
 *
 * ⚠️ **Six is a CONTRACT, not a preference, and there is a second reason for it
 * beyond the 422.** The Xero push casts our decimal string with `Number()` on
 * the way out (`xero-quote.payload.ts:772`) — the exact float conversion this
 * repo bans everywhere else. That is the backend's code and not ours to fix, and
 * six decimal places is comfortably inside what a double represents exactly for
 * these magnitudes, so the cast is safe *only while the limit holds*. A rate
 * carrying more precision than this could not survive the trip even if the
 * column accepted it. So {@link isValidCurrencyRate} enforces the limit rather
 * than advising it.
 */
export const CURRENCY_RATE_MAX_DP = 6;

/**
 * The backend's shape for the field, mirrored exactly: up to 12 integer digits,
 * optionally up to 6 decimals, no sign. A leading `-` is refused here rather
 * than sent and refused there.
 */
const CURRENCY_RATE_PATTERN = /^\d{1,12}(\.\d{1,6})?$/;

/** Base-currency equivalents are money, so they land on 2dp like every other figure. */
const BASE_AMOUNT_DP = 2;

/* ────────────────────────── exact decimal primitives ────────────────────────── */

interface Scaled {
  /** The value with its decimal point removed. */
  units: bigint;
  /** How many digits were after the point. */
  scale: number;
}

/**
 * Parses a decimal string exactly. Signed, because a document total can
 * legitimately be negative (a credit) and refusing that would silently blank the
 * base-currency figure on exactly the document someone is checking.
 */
function parseDecimal(value: string): Scaled | null {
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

/** Renders scaled units back to a fixed-point string. */
function formatScaled({ units, scale }: Scaled): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const body =
    scale === 0
      ? digits
      : `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  return negative ? `-${body}` : body;
}

/**
 * Integer division rounding half away from zero — the same rule `decimal.ts`
 * applies, so a base-currency figure rounds the way every other figure in this
 * app rounds. Half-to-even would be defensible in isolation and would make two
 * money helpers in one codebase disagree on `.005`, which is worse.
 */
function divideRoundHalf(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const a = numerator < 0n ? -numerator : numerator;
  const b = denominator < 0n ? -denominator : denominator;
  const quotient = (a * 2n + b) / (b * 2n);
  return negative ? -quotient : quotient;
}

/* ───────────────────────────────── the model ────────────────────────────────── */

/**
 * A rate with its direction stated, so a caller cannot apply it backwards.
 *
 * Build one with {@link exchangeRateOf} rather than by hand. The builder is
 * where every "there is nothing to show" case is decided — no base currency, no
 * document currency, the same currency on both sides, no pinned rate, a
 * malformed rate, a zero rate — so a value of this type means *there is a real
 * cross-currency conversion to display*, and every function taking one can be
 * total.
 */
export interface ExchangeRate {
  /** The organisation's base currency, e.g. "GBP". Xero's, from `/xero/connection`. */
  baseCurrency: string;
  /** The document's own currency, e.g. "AUD". */
  documentCurrency: string;
  /**
   * Units of `documentCurrency` per **1** `baseCurrency`, as an exact decimal
   * string — the right-hand side of "1 GBP = 1.89207 AUD".
   *
   * ⚠️ Never `Number()` this. It divides a money amount; a float here is the
   * penny of drift the whole module exists to prevent.
   */
  ratePerBase: string;
}

/**
 * Is this a rate the API would accept, AND one worth applying?
 *
 * Two rules, and the second is ours alone:
 *
 *  - **Non-negative, at most 6dp.** The backend's own validation. A value
 *    failing this is a 422 waiting to happen, so a form should refuse it before
 *    the request rather than after.
 *  - ⚠️ **Not zero — refused here, ACCEPTED there.** `0` and `0.000000` match
 *    the backend's non-negative regex and would be stored. They are nonsense as
 *    a rate: a zero divisor is not a conversion at all, and rendering it would
 *    print every base-currency total as `0.00` beside a real document total —
 *    a figure that looks measured and means "we divided by nothing". Refusing it
 *    client-side is deliberately stricter than the server, which is always the
 *    safe direction to be.
 *
 * Note this validates the SHAPE and MAGNITUDE only. It cannot say whether the
 * number points the right way; see the direction warning at the top of the file.
 */
export function isValidCurrencyRate(value: string): boolean {
  const trimmed = value.trim();
  if (!CURRENCY_RATE_PATTERN.test(trimmed)) return false;

  // "0", "0.0", "0.000000" — all zero, none of them a rate.
  const parsed = parseDecimal(trimmed);
  return parsed !== null && parsed.units !== 0n;
}

/**
 * The one place the app's bare `currencyRate` becomes a DIRECTED rate.
 *
 * Returns `null` — meaning "print no rate line and no base-currency figure" —
 * for every case where there is genuinely nothing to say:
 *
 *  - **no base currency.** Xero is disconnected, or the connection read failed.
 *    `baseCurrency` is `string | null` on `XeroConnection` for exactly that.
 *  - **no document currency.** `currency` is genuinely nullable on a quote and
 *    an invoice.
 *  - **the document is already in the base currency.** Nothing to convert and no
 *    rate line to print. "1 GBP = 1 GBP" is noise, and a GBP figure repeated
 *    under a GBP figure invites the reader to look for the difference.
 *  - ⚠️ **no rate pinned.** `currencyRate: null` is the ORDINARY case — Xero
 *    then applies its own daily rate and we do not know what it will be.
 *    **Never `?? 1`**: that asserts parity between two currencies, which is a
 *    made-up number rather than a missing one. **Never `?? 0`** either. `null`
 *    in, `null` out, and the UI says nothing.
 *  - **a rate that fails {@link isValidCurrencyRate}**, zero included.
 *
 * Currency codes are compared exactly rather than case-folded. Both sides come
 * from the server as `^[A-Z]{3}$` and this app case-folds nowhere (`?currency=usd`
 * is a 422 on `/quotes`), so folding here would only serve to hide a malformed
 * code that ought to be visible.
 */
export function exchangeRateOf(input: {
  /** From `XeroConnection.baseCurrency`. */
  baseCurrency: string | null | undefined;
  /** The document's `currency`. */
  documentCurrency: string | null | undefined;
  /**
   * The document's `currencyRate`, verbatim.
   *
   * ⚠️ Interpreted as units of `documentCurrency` per 1 `baseCurrency`. That
   * reading is UNCONFIRMED — see the file header. This is the line to change if
   * the backend says it is the inverse; nothing else in the app interprets the
   * field.
   *
   * ⚠️ And note what this field IS: the Xero push sends it unconverted as
   * `Quote.CurrencyRate`. A control that edits it is editing a real accounting
   * document, not a preview.
   */
  currencyRate: string | null | undefined;
}): ExchangeRate | null {
  const baseCurrency = input.baseCurrency?.trim() ?? '';
  const documentCurrency = input.documentCurrency?.trim() ?? '';
  if (baseCurrency === '' || documentCurrency === '') return null;
  if (baseCurrency === documentCurrency) return null;

  /*
   * ⚠️ Note what is NOT tested here: the rate's VALUE. Suppression is decided
   * one line above, on the currencies matching — never on `rate === 1`.
   *
   * The backend's phrasing, kept because it is the whole distinction in one
   * line: **1 is not a missing rate, it is a correct rate carrying no
   * information.** A same-currency document genuinely comes back as 1 (1 GBP
   * really is 1 GBP), and that case is already gone by the time we get here.
   * Testing the value instead would ALSO hide a real parity, if two different
   * currencies ever sat at 1.000000 — a conversion worth showing, suppressed
   * because its answer happened to be one.
   *
   * Measured, not assumed: 100 of 100 live Xero invoices carry a rate, so the
   * "Xero returned nothing" branch this file once planned for is unreachable.
   * Xero sends six decimals (`"1.195340"`), which `CURRENCY_RATE_PATTERN`
   * accepts; `isValidCurrencyRate` rejects only zero.
   */
  const rate = input.currencyRate?.trim() ?? '';
  if (rate === '' || !isValidCurrencyRate(rate)) return null;

  return { baseCurrency, documentCurrency, ratePerBase: rate };
}

/**
 * A document-currency amount expressed in the base currency. Exact.
 *
 * `documentAmount / ratePerBase`, taken to 2dp in ONE exact division on scaled
 * BigInts — never two, and never through a float.
 *
 * ⚠️ **ROUNDING: once, at the end, half away from zero. Convert the TOTAL; do
 * not total the conversions.**
 *
 * The precedent worth naming is the basket rule in CLAUDE.md, and this case is
 * its mirror image rather than its twin. There, the database rounds per line and
 * the client sums the server's already-rounded `lineTotal`s, because the stored
 * per-line figure is the one Xero reads and the client must agree with it. Here
 * there is **no stored base-currency figure anywhere** — not on a line, not on
 * the header, not in Xero. Nothing exists to agree with, so the only question is
 * which arithmetic is least wrong, and converting the one already-rounded
 * document total rounds once instead of N times.
 *
 * The consequence a caller must not paper over: **a column of per-line base
 * equivalents will not always add up to the converted total.** Both figures are
 * correctly rounded and they can differ by a penny or two. If a UI shows both,
 * the converted TOTAL is the one to trust and the column is indicative — say so,
 * or better, do not render both.
 *
 * ⚠️ Returns `null`, never `"0.00"`, for a missing or malformed amount, and for
 * a rate that fails {@link isValidCurrencyRate}. `null` means "no equivalent to
 * show"; `"0.00"` asserts that the document is worth nothing in the base
 * currency, which is a different and much louder claim. `null` propagates the
 * way it does everywhere else in `decimal.ts`.
 *
 * `documentAmount` is widened to `string | null` deliberately: `subTotal` and
 * `grandTotal` are NOT NULL but `discountTotal` and `taxTotal` are nullable, and
 * a caller should be able to hand any of the four straight in without a guard
 * that would only re-derive this one.
 *
 * This does arithmetic and does not re-check currency codes — that decision was
 * taken in {@link exchangeRateOf}, and taking it twice is how two copies of one
 * rule drift.
 */
export function toBaseCurrency(
  documentAmount: string | null | undefined,
  rate: ExchangeRate,
): string | null {
  if (typeof documentAmount !== 'string') return null;

  const amount = parseDecimal(documentAmount);
  if (amount === null) return null;

  // Re-validated rather than trusted: a hand-built ExchangeRate can carry a zero,
  // and a zero denominator here would throw rather than render a wrong number.
  if (!isValidCurrencyRate(rate.ratePerBase)) return null;
  const perBase = parseDecimal(rate.ratePerBase);
  if (perBase === null || perBase.units === 0n) return null;

  /*
   * base = (amount.units / 10^amount.scale) / (perBase.units / 10^perBase.scale)
   *
   * rearranged so the whole thing is one integer division, and multiplied by
   * 10^BASE_AMOUNT_DP so the quotient comes out already in cent units. Splitting
   * this into "divide, then round to 2dp" would round twice.
   */
  const units = divideRoundHalf(
    amount.units * 10n ** BigInt(perBase.scale) * 10n ** BigInt(BASE_AMOUNT_DP),
    perBase.units * 10n ** BigInt(amount.scale),
  );

  return formatScaled({ units, scale: BASE_AMOUNT_DP });
}

/**
 * "1 GBP = 1.89207 AUD" — the label Xero prints above a converted total.
 *
 * Total by construction: the only way to hold an {@link ExchangeRate} is
 * {@link exchangeRateOf}, which refuses to build one for a same-currency
 * document, so there is no degenerate "1 GBP = 1 GBP" case to guard against
 * here. A caller who hand-builds one gets what they asked for.
 *
 * Trailing zeros are stripped from the fraction as TEXT — `"1.892070"` reads as
 * `"1.89207"` and `"2.000000"` as `"2"` — because the column is `DECIMAL(18,6)`
 * and pads, while Xero shows the significant figures. This is a display
 * transform only: it never touches the value that goes back to the server, which
 * must stay byte-for-byte what the server sent.
 *
 * ⚠️ **No source and no as-at date, unlike Xero's own line** ("Source: XE,
 * 27 Aug 2026"). Our `currencyRate` is a bare number with no provenance
 * whatsoever: nothing records where it came from or when it was fixed, and the
 * likeliest answer is that somebody typed it. Printing "Source: XE" beside it
 * would attribute a hand-entered figure to a rate provider — a claim no field in
 * this system supports, and one that would make a guessed rate read as a
 * measured one.
 *
 * That is not a gap waiting to be filled. The owner ruled out the external rate
 * API by name, so there is no provider to name and no fetch date to print, and
 * adding either would mean inventing both.
 */
export function formatRateLine(rate: ExchangeRate): string {
  return `1 ${rate.baseCurrency} = ${trimTrailingZeros(rate.ratePerBase)} ${rate.documentCurrency}`;
}

/** Textual only — no `Number()`, no `parseFloat`, nothing that could round. */
function trimTrailingZeros(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.includes('.')) return trimmed;
  return trimmed.replace(/0+$/, '').replace(/\.$/, '');
}
